// CurrentTax.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronLeft,
  Search,
  RefreshCw,
  Save,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

/**
 * Current Tax (ASPX -> React)
 *
 * APIs (Node):
 * GET  /api/current-tax/list?page&limit&search
 * POST /api/current-tax/save   { taxValue, fromDate, adminId }
 */
const CurrentTax = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = useMemo(() => `bg-${baseColor}-${baseShade}`, [baseColor, baseShade]);
  const hoverClass = useMemo(() => `hover:bg-${baseColor}-700`, [baseColor]);
  

  const [showForm, setShowForm] = useState(true);

  // form
  const [taxValue, setTaxValue] = useState("");
  const [fromDate, setFromDate] = useState(""); // use YYYY-MM-DD for <input type="date">
  const [saving, setSaving] = useState(false);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // search + paging
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const recordsPerPage = 20;
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // finyear header (optional)
  const finYear = useMemo(() => {
    // if you have "finYear" stored like "2025-2026", this will work.
    const a = (localStorage.getItem("finYear") || "").trim();
    const b = (localStorage.getItem("finYearText") || "").trim();
    return a || b; // else empty -> backend uses active DB
  }, []);
  const headers = useMemo(() => (finYear ? { "x-finyear": finYear } : {}), [finYear]);

  const getAdminCredId = () => {
    // ASPX Session["AdminID"] == tbl_Admin_Credential.admin_credential_id
    try {
      const rawAdmin = localStorage.getItem("admin");
      if (rawAdmin) {
        const admin = JSON.parse(rawAdmin);
        const id = Number(admin?.id || 0);
        if (id) return id;
      }
    } catch {}

    // fallbacks if you stored separately
    const a = Number(localStorage.getItem("adminCredId") || 0);
    if (a) return a;

    const b = Number(localStorage.getItem("adminId") || 0);
    return b || 0;
  };

  const fetchList = async (p = 1, s = search) => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/current-tax/list`, {
        params: { page: p, limit: recordsPerPage, search: s || "" },
        headers,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load tax list");
        return;
      }

      setRows(res.data.list || []);
      setPage(res.data.page || p);
      setTotalPages(res.data.totalPages || 1);
      setTotalRecords(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to load tax list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // debounce search input -> search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // when search changes, reload page 1
  useEffect(() => {
    fetchList(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const validate = () => {
    if (!String(fromDate || "").trim()) {
      toast.error("Please enter From date");
      return false;
    }

    if (!String(taxValue || "").trim()) {
      toast.error("Please enter tax");
      return false;
    }

    const v = Number(taxValue);
    if (Number.isNaN(v)) {
      toast.error("Invalid tax value");
      return false;
    }

    if (v > 100) {
      toast.error("Tax must be less than 100");
      return false;
    }

    // prevent future date like ASPX
    const selected = new Date(fromDate + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selected.getTime() > today.getTime()) {
      toast.error("You can select only current or Previous days");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const adminId = getAdminCredId();
    if (!adminId) {
      toast.error("AdminID missing. Please login again.");
      return;
    }

    try {
      setSaving(true);

      const res = await axios.post(
        `${BASE_URL}/api/current-tax/save`,
        {
          taxValue: Number(taxValue),
          fromDate, // YYYY-MM-DD is supported by node parser
          adminId,
        },
        { headers }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Please retry.");
        return;
      }

      toast.success(res.data?.message || "Saved successfully");
      setFromDate("");
      setTaxValue("");
      await fetchList(1, search);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const start = Math.max(1, page - 2);
      const end = Math.min(totalPages, start + 4);

      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages) pages.push("...");
    }

    return pages;
  };

  const handlePageChange = (p) => {
    if (!p || p === "..." || p === page) return;
    fetchList(p, search);
  };

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Current Tax Details</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header row */}
          <div className="flex justify-between items-center gap-3 flex-wrap mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="button"
                onClick={() => (window.location.href = "/adminMaster/home")}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
                title="Back"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>

              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Current Tax
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRecords} Records
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border shadow-sm ${
                  isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                }`}
              >
                <Search className="w-4 h-4 opacity-70" />
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search (date or tax)..."
                  className={`outline-none bg-transparent text-sm w-[240px] ${
                    isDark ? "text-white placeholder:text-white/50" : "text-black placeholder:text-gray-400"
                  }`}
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  fetchList(1, "");
                  toast.info("Refreshed");
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                <RefreshCw className="w-5 h-5" />
                Refresh
              </button>

              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                {showForm ? "Hide Entry Form" : "Show Entry Form"}
              </button>
            </div>
          </div>

          {/* Form */}
          <div
            className={`rounded-xl border mb-6 overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {showForm && (
              <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
                <div className="grid grid-cols-12 gap-4 items-end max-w-4xl">
                  {/* Tax value */}
                  <div className="col-span-12 md:col-span-4">
                    <label className="text-sm font-medium block mb-1">Tax value</label>
                    <input
                      value={taxValue}
                      onChange={(e) => {
                        // allow only numbers + dot, max length 5 like ASPX
                        const v = e.target.value.replace(/[^\d.]/g, "").slice(0, 5);
                        setTaxValue(v);
                      }}
                      disabled={saving}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                      }`}
                      placeholder="e.g. 18"
                    />
                    <div className="text-xs mt-1 opacity-70">Tax must be ≤ 100</div>
                  </div>

                  {/* From date */}
                  <div className="col-span-12 md:col-span-4">
                    <label className="text-sm font-medium block mb-1">From Date</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      disabled={saving}
                      max={new Date().toISOString().slice(0, 10)} // prevent future date
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                      }`}
                    />
                    <div className="text-xs mt-1 opacity-70">Only current or previous days</div>
                  </div>

                  {/* Save */}
                  <div className="col-span-12 md:col-span-4 flex md:justify-end">
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                    >
                      <Save className="w-5 h-5" />
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto max-w-full">
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="min-w-[600px] w-full">
                  <thead className={`${buttonClass} text-white sticky top-0 z-10`}>
                    <tr>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                        S.No
                      </th>
                      <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                        From Date
                      </th>
                      <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">
                        Tax
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-6 text-center opacity-70">
                          Loading...
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-6 text-center opacity-70">
                          No Records Found
                        </td>
                      </tr>
                    ) : (
                      rows.map((r, idx) => (
                        <tr
                          key={`${r.fld_FromDate}-${idx}`}
                          className={`transition-colors duration-200 ${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-gray-50"
                              : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                          }`}
                        >
                          <td className="px-3 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700">
                            {(page - 1) * recordsPerPage + (idx + 1)}
                          </td>
                          <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                            {r.fld_FromDate ? new Date(r.fld_FromDate).toLocaleDateString("en-GB") : ""}
                          </td>
                          <td className="px-3 py-3 text-sm text-right">
                            {r.fld_taxValue ?? ""}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div
              className={`flex items-center justify-between gap-3 p-4 border-t ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div className="text-sm opacity-80">
                Page <b>{page}</b> / {totalPages}
              </div>

              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button
                  type="button"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1 || loading}
                  className={`px-3 py-2 rounded-lg border text-sm font-semibold transition disabled:opacity-50 ${
                    isDark
                      ? "bg-gray-800 border-gray-700 hover:bg-gray-700 text-white"
                      : "bg-white border-gray-300 hover:bg-gray-50 text-gray-800"
                  }`}
                >
                  Prev
                </button>

                {renderPageNumbers().map((p, i) => (
                  <button
                    key={`${p}-${i}`}
                    type="button"
                    onClick={() => handlePageChange(p)}
                    disabled={p === "..." || loading}
                    className={`px-3 py-2 rounded-lg border text-sm font-semibold transition disabled:opacity-50 ${
                      p === page
                        ? `${buttonClass} text-white border-transparent`
                        : isDark
                        ? "bg-gray-800 border-gray-700 hover:bg-gray-700 text-white"
                        : "bg-white border-gray-300 hover:bg-gray-50 text-gray-800"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages || loading}
                  className={`px-3 py-2 rounded-lg border text-sm font-semibold transition disabled:opacity-50 ${
                    isDark
                      ? "bg-gray-800 border-gray-700 hover:bg-gray-700 text-white"
                      : "bg-white border-gray-300 hover:bg-gray-50 text-gray-800"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default CurrentTax;
